import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Send notification to a specific user
export const sendNotification = async (
  userId: string,
  type: string,
  title: string,
  message: string | null,
  relatedId: string | null = null,
  relatedType: string | null = null
) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type,
        title,
        message,
        related_id: relatedId,
        related_type: relatedType,
      });

    if (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  } catch (error) {
    console.error('Failed to send notification:', error);
  }
};

// Send notification to all event participants
export const notifyEventParticipants = async (
  eventId: string,
  type: string,
  title: string,
  message: string,
  excludeUserId?: string
) => {
  try {
    // Fetch all participants
    const { data: participants, error: fetchError } = await supabase
      .from('event_participants')
      .select('user_id')
      .eq('event_id', eventId)
      .in('status', ['going', 'maybe']);

    if (fetchError) {
      console.error('Error fetching participants:', fetchError);
      return;
    }

    if (!participants || participants.length === 0) return;

    // Send notification to each participant (except excluded user)
    const notifications = participants
      .filter(p => p.user_id !== excludeUserId)
      .map(p => ({
        user_id: p.user_id,
        type,
        title,
        message,
        related_id: eventId,
        related_type: 'event',
      }));

    if (notifications.length > 0) {
      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications);

      if (insertError) {
        console.error('Error sending notifications to participants:', insertError);
      }
    }
  } catch (error) {
    console.error('Failed to notify participants:', error);
  }
};

// Notify event host
export const notifyEventHost = async (
  eventId: string,
  type: string,
  title: string,
  message: string
) => {
  try {
    // Fetch event host
    const { data: event, error: fetchError } = await supabase
      .from('events')
      .select('created_by')
      .eq('id', eventId)
      .single();

    if (fetchError || !event) {
      console.error('Error fetching event:', fetchError);
      return;
    }

    await sendNotification(event.created_by, type, title, message, eventId, 'event');
  } catch (error) {
    console.error('Failed to notify event host:', error);
  }
};
